import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Copy, PackageCheck, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError, customerApi, uploadFile, type UploadTicket } from "@repair/api";
import { bytesLabel, formatDateTime, type ActionDescriptor } from "@repair/contracts";
import {
  Button,
  AttachmentGallery,
  ConfirmDialog,
  EmptyState,
  EventTimeline,
  Field,
  LifecycleProgress,
  LoadingState,
  PageBanner,
  Section,
  StatusHero,
  TextArea,
  TextInput,
  IconButton,
} from "@repair/ui";

type SupplementUpload = {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  ticket?: UploadTicket;
  error?: string;
};

export function DetailPage() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ["customer-order", id], queryFn: () => customerApi.detail(id), enabled: Boolean(id) });
  const [selectedAction, setSelectedAction] = useState<ActionDescriptor | null>(null);
  const [note, setNote] = useState("");
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [uploads, setUploads] = useState<SupplementUpload[]>([]);
  const [conflict, setConflict] = useState("");

  const execute = useMutation({
    mutationFn: async () => {
      if (!selectedAction || !detail.data) throw new Error("请选择操作");
      if (selectedAction.code === "SUBMIT_SUPPLEMENT") {
        if (uploads.some((item) => item.status === "pending" || item.status === "uploading")) throw new Error("附件仍在上传");
        const failed = uploads.filter((item) => item.status === "error");
        if (failed.length) throw new Error(`${failed.length} 个附件上传失败，请重试或移除`);
        for (const entry of uploads) {
          if (entry.ticket) await customerApi.attach(detail.data.item.id, entry.ticket, "customer_supplement");
        }
      }
      return customerApi.execute(detail.data.item.id, selectedAction, detail.data.state.version, {
        note,
        carrier,
        tracking_no: trackingNo,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["customer-order", id], data);
      void queryClient.invalidateQueries({ queryKey: ["customer-orders"] });
      setSelectedAction(null);
      setNote("");
      setUploads([]);
      setConflict("");
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "STATE_CONFLICT") {
        setConflict("工单刚刚已更新，页面已重新读取最新状态。");
        void detail.refetch();
      }
    },
  });

  async function addSupplementFiles(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const entry: SupplementUpload = { id: crypto.randomUUID(), file, progress: 0, status: "pending" };
      setUploads((items) => [...items, entry]);
      await startSupplementUpload(entry);
    }
  }

  async function startSupplementUpload(entry: SupplementUpload) {
    if (!detail.data) return;
    if (entry.file.size > 50 * 1024 * 1024) {
      updateSupplementUpload(entry.id, { status: "error", error: "单个文件不能超过 50MB" });
      return;
    }
    try {
      updateSupplementUpload(entry.id, { status: "uploading", progress: 0.05, error: undefined });
      const ticket = await customerApi.prepareUpload(detail.data.item.id, entry.file);
      if (!ticket) throw new Error("上传凭证生成失败");
      await uploadFile(ticket, entry.file, (progress) => updateSupplementUpload(entry.id, { progress }));
      updateSupplementUpload(entry.id, { status: "success", progress: 1, ticket });
    } catch (error) {
      updateSupplementUpload(entry.id, {
        status: "error",
        error: error instanceof Error ? error.message : "上传失败",
      });
    }
  }

  function updateSupplementUpload(id: string, patch: Partial<SupplementUpload>) {
    setUploads((items) => items.map((item) => item.id === id ? { ...item, ...patch } : item));
  }

  if (detail.isLoading) return <div className="customer-page"><LoadingState label="正在读取工单" /></div>;
  if (detail.error || !detail.data) {
    return <div className="customer-page"><EmptyState title="无法打开工单" detail={detail.error?.message || "工单不存在"} action={<Button onClick={() => void detail.refetch()}>重试</Button>} /></div>;
  }

  const data = detail.data;
  const primaryAction = data.available_actions.find((action) => action.intent === "primary") || data.available_actions[0] || null;
  const quoteQuestionAction = data.available_actions.find((action) => action.code === "QUESTION_QUOTE") || null;
  const inspection = data.inspection_records[0] as Record<string, unknown> | undefined;
  const closed = data.state.terminal;

  return (
    <div className="customer-page">
      {conflict && <PageBanner tone="attention">{conflict}</PageBanner>}
      <StatusHero state={data.state} orderNo={data.item.order_no} />
      <div className="customer-surface">
        <div className="customer-surface__body">
          <LifecycleProgress milestones={data.milestones} compact />
          {data.sla_risk && <PageBanner tone={data.sla_risk.level === "danger" ? "danger" : "attention"}>{data.sla_risk.title}</PageBanner>}

          {data.quote && (
            <Section title="维修方案与报价" meta={`第 ${Math.max(1, data.quote.version_no)} 版`}>
              {Boolean(inspection?.diagnosis) && <p className="business-copy">{String(inspection?.diagnosis)}</p>}
              <div className="quote-items">
                {data.quote.items.map((item, index) => (
                  <div className="quote-item" key={item.id || index}>
                    <span>{item.item_name}<small>{item.description}</small></span>
                    <strong>¥ {(item.amount ?? item.quantity * item.unit_price).toFixed(2)}</strong>
                  </div>
                ))}
              </div>
              <div className="quote-total"><span>合计</span><strong>¥ {data.quote.total_amount.toFixed(2)}</strong></div>
              <div className="detail-facts">
                <div><span>预计工期</span><strong>{data.quote.estimated_days ? `${data.quote.estimated_days} 天` : "待确认"}</strong></div>
                <div><span>有效期</span><strong>{formatDateTime(data.quote.valid_until)}</strong></div>
                <div><span>质保</span><strong>{data.quote.warranty_text || "以维修结果为准"}</strong></div>
                <div><span>客户决定</span><strong>{data.quote.customer_decision || "等待确认"}</strong></div>
              </div>
              {quoteQuestionAction && (
                <Button variant="ghost" onClick={() => setSelectedAction(quoteQuestionAction)}>对报价有疑问</Button>
              )}
            </Section>
          )}

          {data.shipments.length > 0 && (
            <Section title="物流">
              {data.shipments.map((shipment) => (
                <div className="shipment" key={shipment.id}>
                  <div className="shipment__top">
                    <strong>{shipment.direction === "INBOUND" ? "寄往售后" : "维修回寄"}</strong>
                    <span>{shipment.status}</span>
                  </div>
                  <code>{shipment.carrier} · {shipment.tracking_no}</code>
                  <Button variant="ghost" onClick={() => void navigator.clipboard.writeText(shipment.tracking_no)}><Copy size={14} />复制单号</Button>
                </div>
              ))}
            </Section>
          )}

          {data.repair_record && (
            <Section title="维修记录">
              <div className="detail-facts">
                <div><span>维修动作</span><strong>{String(data.repair_record.repair_action || "—")}</strong></div>
                <div><span>维修结果</span><strong>{String(data.repair_record.repair_result || "—")}</strong></div>
              </div>
            </Section>
          )}

          {closed && (
            <Section title={data.state.code === "CLOSED" ? "本次返修已完成" : "本次返修已终止"}>
              <div className="detail-facts">
                <div><span>最终状态</span><strong>{data.state.label}</strong></div>
                <div><span>最后更新</span><strong>{formatDateTime(data.item.updated_at)}</strong></div>
                <div><span>报价</span><strong>{data.quote ? `¥ ${data.quote.total_amount.toFixed(2)}` : "无需报价"}</strong></div>
                <div><span>回寄</span><strong>{data.shipments.find((item) => item.direction === "OUTBOUND")?.tracking_no || "—"}</strong></div>
              </div>
            </Section>
          )}

          <Section title="设备与报修信息">
            <div className="detail-facts">
              <div><span>设备 SN</span><strong>{data.item.sn}</strong></div>
              <div><span>提交时间</span><strong>{formatDateTime(data.item.created_at)}</strong></div>
              <div><span>联系人</span><strong>{data.item.receiver_name}</strong></div>
              <div><span>手机号</span><strong>{data.item.receiver_phone}</strong></div>
            </div>
            <p className="business-copy">{data.item.fault_desc}</p>
          </Section>

          {data.attachments.length > 0 && (
            <Section title="附件" meta={`${data.attachments.length} 个`}>
              <AttachmentGallery items={data.attachments} />
            </Section>
          )}

          <Section title="处理记录"><EventTimeline events={data.timeline} /></Section>
        </div>
      </div>

      {primaryAction && (
        <div className="customer-sticky"><div className="customer-sticky__inner">
          <Button onClick={() => setSelectedAction(primaryAction)}>{primaryAction.label}</Button>
        </div></div>
      )}

      <ConfirmDialog
        open={Boolean(selectedAction)}
        onOpenChange={(open) => !open && setSelectedAction(null)}
        title={selectedAction?.label || "确认操作"}
        description={selectedAction ? `${data.state.label} → ${selectedAction.result_label}` : undefined}
        confirmLabel={selectedAction?.label || "确认"}
        danger={selectedAction?.intent === "danger"}
        busy={execute.isPending}
        onConfirm={() => execute.mutate()}
      >
        <div className="action-form">
          {selectedAction?.code === "SUBMIT_INBOUND_TRACKING" && (
            <>
              <Field label="物流公司"><TextInput value={carrier} onChange={(event) => setCarrier(event.target.value)} /></Field>
              <Field label="物流单号"><TextInput value={trackingNo} onChange={(event) => setTrackingNo(event.target.value)} /></Field>
            </>
          )}
          {selectedAction?.code === "SUBMIT_SUPPLEMENT" && (
            <Field label="补充附件" hint="图片、视频或 PDF，单个不超过 50MB">
              <TextInput type="file" multiple accept="image/*,video/*,.pdf" onChange={(event) => void addSupplementFiles(event.target.files)} />
              <div className="upload-list">
                {uploads.map((entry) => (
                  <div className="upload-item" key={entry.id}>
                    <strong>{entry.file.name}</strong>
                    <div>
                      {entry.status === "error" && (
                        <IconButton label="重试" onClick={() => void startSupplementUpload(entry)}><RotateCcw size={16} /></IconButton>
                      )}
                      <IconButton label="移除" onClick={() => setUploads((items) => items.filter((item) => item.id !== entry.id))}><Trash2 size={16} /></IconButton>
                    </div>
                    <span>{entry.error || `${bytesLabel(entry.file.size)} · ${supplementUploadStatus(entry.status)}`}</span>
                    <div className="upload-progress"><i style={{ width: `${entry.progress * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            </Field>
          )}
          {selectedAction?.required_fields.includes("note") && (
            <Field label={selectedAction.intent === "danger" ? "原因" : "补充说明"}>
              <TextArea value={note} onChange={(event) => setNote(event.target.value)} />
            </Field>
          )}
          {selectedAction?.code === "CONFIRM_QUOTE" && data.quote && (
            <PageBanner tone="attention">确认后将按 ¥ {data.quote.total_amount.toFixed(2)} 进入维修。</PageBanner>
          )}
          {selectedAction?.code === "CONFIRM_RECEIPT" && (
            <PageBanner tone="info"><PackageCheck size={16} />请确认已收到设备且外观、功能无异常。</PageBanner>
          )}
          {execute.error && <PageBanner tone="danger"><AlertTriangle size={16} />{execute.error.message}</PageBanner>}
        </div>
      </ConfirmDialog>
    </div>
  );
}

function supplementUploadStatus(status: SupplementUpload["status"]) {
  return { pending: "等待上传", uploading: "上传中", success: "已完成", error: "上传失败" }[status];
}
